import React from "react";
import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { formatDate } from "../../config/uiHelpers";

export default function StudentDetailsUI() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // Get all data from navigation state
  const studentData = location.state || {};

  const {
    user_id,
    course_name,
    email,
    language,
    first_name,
    last_name,
    full_name,
    gender,
    mobile_number,
    avatar_url,
    created_at,
    course_id,
    course_created_at,
    schedule_start_date,
    schedule_end_date,
    enrollment_date, // ✅ This is the admission date
    course_type,
    alternate_phone, 
  } = studentData;

  // If no data is passed, show error
  if (!studentData || Object.keys(studentData).length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Students</h1>
          </div>
          <div className="p-10 text-center text-gray-600 bg-white rounded-lg shadow-sm border border-gray-200">
            No student data available. Please go back and select a student.
          </div>
        </div>
      </div>
    );
  }

  /* ===============================
     Derived / Computed Values
  ================================ */

  const displayName =
    full_name || `${first_name || ""} ${last_name || ""}`.trim() || "Unknown User";

  // For now, using placeholder values for grade data
  // These would come from actual grade data in a real implementation
  const gradePercentage = 0; // Placeholder
  const earnedPoints = 0; // Placeholder
  const totalPoints = 1; // Placeholder
  const minimumGradePercentage = 75; // Placeholder

  const currentYear = new Date().getFullYear();

  // Duration calculation (days)
  const calculateDurationInDays = (start, end) => {
    if (!start || !end) return "-";

    const startDate = new Date(start);
    const endDate = new Date(end);

    const diffTime = endDate - startDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))+1; // +1 to include both start and end dates

    return `${diffDays} days`;
  };

  const courseDuration = calculateDurationInDays(
    schedule_start_date,
    schedule_end_date,
  );

  const formatLanguage = (lang) => {
    if (!lang) return "-";

    const languageMap = {
      en: "English",
      es: "Spanish",
      fr: "French",
      hi: "Hindi",
      ta: "Tamil",
    };

    return languageMap[lang.toLowerCase()] || lang;
  };

  const displayLanguage = formatLanguage(language);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold text-gray-900">Students</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT PANEL */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Profile Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-4 mb-3">
                <img
                  className="w-16 h-16 rounded-full object-cover"
                  src={
                    avatar_url ||
                    "https://placehold.co/64x64/4AACC5/white?text=" +
                      displayName.charAt(0)
                  }
                  alt={displayName}
                  onError={(e) => {
                    e.target.src =
                      "https://placehold.co/64x64/4AACC5/white?text=" +
                      displayName.charAt(0);
                  }}
                />
                <div>
                  <span className="inline-block px-3 py-1 bg-[#4AACC5] text-white text-xs rounded-full mb-2">
                    ID : {user_id}
                  </span>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {displayName}
                  </h2>
                </div>
              </div>
            </div>

            {/* General Information */}
            <div className="p-6 border-b border-gray-200">
              <h3 className="flex items-start text-sm font-semibold text-gray-900 mb-4">
                General Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="flex items-start text-xs text-gray-600 ">
                    Gender
                  </span>
                  <span className="flex items-start text-sm text-gray-900 font-medium">
                    {gender || "-"}
                  </span>
                </div>

                <div>
                  <span className="flex items-start text-xs text-gray-600">
                    Admission Date
                  </span>
                  <span className="flex items-start text-sm text-gray-900 font-medium">
                    {formatDate(enrollment_date)}
                  </span>
                </div>

                <div>
                  <span className="flex items-start text-xs text-gray-600 ">
                    Student Type
                  </span>
                  <span className="flex items-start text-sm text-gray-900 font-medium">
                    {course_type || "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* Program Information */}
            <div className="p-6 border-b border-gray-200">
              <h3 className="flex items-start text-sm font-semibold text-gray-900 mb-4">
                Program Information
              </h3>

              {/* Program Name */}
              <div className="mb-4">
                <span className="flex items-start text-xs text-gray-600">
                  Program Name
                </span>
                <span className="flex items-start text-sm text-gray-900 font-medium truncate">
                  {course_name || "-"}
                </span>
              </div>

              {/* Language & Duration in Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="flex items-start text-xs text-gray-600">
                    Language
                  </span>
                  <span className="flex items-start text-sm text-gray-900 font-medium">
                    {displayLanguage || "-"}
                  </span>
                </div>

                <div>
                  <span className="flex items-start text-xs text-gray-600">
                    Duration
                  </span>
                  <span className="flex items-start text-sm text-gray-900 font-medium">
                    {courseDuration || "-"}
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="p-6">
              <h3 className="flex items-start text-sm font-semibold text-gray-900 mb-4">
                Contact Information
              </h3>

              <div className="grid grid-cols-2 gap-8">
                <div>
                  <span className="flex items-start text-xs text-gray-600 ">
                    Email Address
                  </span>
                  <span className="flex items-start text-sm text-gray-900 font-medium break-all">
                    {email || "-"}
                  </span>
                </div>

                <div>
                  <span className="flex items-start text-xs text-gray-600">
                    Mobile Number
                  </span>
                  <span className="flex items-start text-sm text-gray-900 font-medium">
                    {mobile_number || "-"}
                  </span>
                </div>

                <div>
                  <span className="flex items-start text-xs text-gray-600 ">
                    Secondary Mobile
                  </span>
                  <span className="flex items-start text-sm text-gray-900 font-medium">
                    {alternate_phone || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Student Grade
              </h3>
              <div className="px-3 py-1 border border-gray-300 rounded">
                <span className="text-sm text-gray-700">{currentYear}</span>
              </div>
            </div>

            <h4 className="text-base font-semibold text-gray-900 mb-2">
              {displayName} did{" "}
              {gradePercentage >= minimumGradePercentage
                ? "pass"
                : "not pass"}{" "}
              the program
            </h4>

            <p className="text-sm text-gray-600 mb-6">
              {gender === "Female" ? "She" : gender === "Male" ? "He" : "They"} achieved {gradePercentage}% of the total points in the program
            </p>

            <div className="relative w-full h-8 bg-gray-200 rounded mb-4">
              <div
                className="h-8 bg-emerald-500 rounded transition-all duration-300"
                style={{ width: `${gradePercentage}%` }}
              ></div>

              <div
                className="absolute top-0 bottom-0 w-0.5 bg-emerald-700"
                style={{
                  left: `${minimumGradePercentage}%`,
                }}
              ></div>
            </div>

            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-emerald-500 rounded-sm"></span>
                <span className="text-xs text-gray-700">Earned Points</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-gray-300 rounded-sm"></span>
                <span className="text-xs text-gray-700">Available Points</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-emerald-700 rounded-sm"></span>
                <span className="text-xs text-gray-700">
                  Minimum required grade (
                  {minimumGradePercentage}%)
                </span>
              </div>
            </div>

            <button
              onClick={() => navigate("/ins_student/student_payment")}
              className="px-6 py-2.5 bg-[#4AACC5] text-white rounded text-sm font-medium hover:bg-[#3d8fa3]"
            >
              View Payments
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}